import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Lenis from "lenis";
import Reveal, { EASE } from "@/components/Reveal";
import Cursor from "@/components/Cursor";
import MusicToggle from "@/components/MusicToggle";
import EnvelopeGate from "@/components/EnvelopeGate";
import STRINGS from "@/i18n/engagement";
import {
  FoliageCluster,
  JasmineGarland,
  ChainDiya,
  VineBranch,
  PalaceArch,
  FloralDivider,
  Flower,
  CornerFlourish,
} from "@/components/Ornaments";

/* Guests RSVP through this Google Form (owned by the bride's father,
   who is organising the event). */
const RSVP_FORM_URL = "https://forms.gle/m1h83MV2Ui4jq1wW9";

const GCAL_URL =
  "https://calendar.google.com/calendar/render?action=TEMPLATE" +
  "&text=" +
  encodeURIComponent("Sagai — Divyanshi & Divya Shiv") +
  "&dates=20261103T103000Z/20261103T163000Z" +
  "&details=" +
  encodeURIComponent("Godbharai, Bariksha & Ring Ceremony") +
  "&location=" +
  encodeURIComponent("Hotel SB Castle, Near Govind Nagar Bridge, Fazalganj, Kanpur");

const TARGET = new Date("2026-11-03T16:00:00+05:30").getTime();

const UNIT_KEYS = ["days", "hours", "mins", "secs"];

function getTimeLeft() {
  const d = Math.max(0, TARGET - Date.now());
  return {
    days: String(Math.floor(d / 86400000)),
    hours: String(Math.floor(d / 3600000) % 24).padStart(2, "0"),
    mins: String(Math.floor(d / 60000) % 60).padStart(2, "0"),
    secs: String(Math.floor(d / 1000) % 60).padStart(2, "0"),
  };
}

function loadLang() {
  try {
    const v = localStorage.getItem("eng-lang");
    if (v === "hi" || v === "en") return v;
  } catch {
    /* private mode */
  }
  return "en";
}

/* A wind-swept blossom branch reaching in from the page's left edge.
   Three of its flowers loop forever: break off, tumble, fade — then
   bud again on the branch and bloom back. */
function WindBranch() {
  /* leaf bunches — the same leaves-only clusters as the countdown's
     corners — gathered at the branch's forks */
  const bunches = [
    { t: "translate(58 54) rotate(-115) scale(0.42)" },
    { t: "translate(208 58) rotate(-102) scale(0.5)" },
    { t: "translate(330 28) rotate(-108) scale(0.4)" },
    { t: "translate(378 86) rotate(62) scale(0.38)" },
    { t: "translate(498 94) rotate(-98) scale(0.4)" },
    { t: "translate(438 18) rotate(-100) scale(0.32)" },
    { t: "translate(162 32) rotate(-112) scale(0.3)" },
    { t: "translate(596 102) rotate(-92) scale(0.28)" },
  ];
  const falling = [
    { x: 300, y: 42, fill: "#d9a3a3", center: "#b97b84", s: 1.2, dur: 11, delay: 0 },
    { x: 430, y: 88, fill: "#f7f2e8", center: "#c3a572", s: 1.1, dur: 14, delay: 4.5 },
    { x: 588, y: 103, fill: "#d9a3a3", center: "#b97b84", s: 1, dur: 17, delay: 9 },
  ];
  return (
    <svg viewBox="0 0 700 220" fill="none" aria-hidden="true">
      <g className="branch-root">
        {/* trunk: thick where it enters, tapering to the first fork */}
        <path
          d="M-4 52 C 70 50, 140 54, 210 60
             L 210 68
             C 140 66, 70 62, -4 70 Z"
          fill="#8a6a3a"
        />
        {/* ARM A: rises from the fork toward the upper right */}
        <path
          d="M208 60 C 280 38, 360 24, 440 20
             L 440 22.5
             C 360 28, 280 44, 208 68 Z"
          fill="#8a6a3a"
        />
        {/* ARM B: carries on low toward the right */}
        <path
          d="M208 62 C 320 78, 430 90, 540 99
             L 540 102
             C 430 94, 320 84, 208 68 Z"
          fill="#8a6a3a"
        />
        {/* bark highlights */}
        <path d="M-4 56 C 70 54, 140 57, 206 62" stroke="#b08d4f" strokeWidth="1" opacity="0.5" />
        <path d="M212 61 C 280 41, 356 27, 432 22" stroke="#b08d4f" strokeWidth="0.8" opacity="0.45" />
        <path d="M212 65 C 320 80, 428 92, 534 100" stroke="#b08d4f" strokeWidth="0.8" opacity="0.45" />
        {/* knots at the forks */}
        <circle cx="210" cy="64" r="3" fill="#6f5430" opacity="0.75" />
        <circle cx="330" cy="31" r="2.4" fill="#6f5430" opacity="0.7" />
        <circle cx="380" cy="87" r="2.4" fill="#6f5430" opacity="0.7" />
        <circle cx="500" cy="96" r="2.2" fill="#6f5430" opacity="0.7" />

        {/* ARM A forks again */}
        <path d="M330 30 C 358 16, 384 9, 410 5" stroke="#8a6a3a" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M440 20 C 456 13, 468 9, 484 6" stroke="#8a6a3a" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M270 48 C 284 36, 296 29, 312 23" stroke="#8a6a3a" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M360 16 C 368 12, 374 9, 382 7" stroke="#8a6a3a" strokeWidth="1.1" strokeLinecap="round" />
        {/* ARM B forks again */}
        <path d="M380 88 C 408 102, 438 112, 468 118" stroke="#8a6a3a" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M500 96 C 520 82, 540 73, 562 66" stroke="#8a6a3a" strokeWidth="2" strokeLinecap="round" />
        <path d="M540 100 C 580 104, 612 106, 640 108" stroke="#8a6a3a" strokeWidth="2" strokeLinecap="round" />
        <path d="M430 108 C 446 115, 458 119, 474 122" stroke="#8a6a3a" strokeWidth="1.3" strokeLinecap="round" />
        <path d="M595 104 C 610 97, 622 93, 636 90" stroke="#8a6a3a" strokeWidth="1.4" strokeLinecap="round" />
        {/* small twigs off the trunk */}
        <path d="M120 56 C 134 44, 148 38, 164 32" stroke="#8a6a3a" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M150 64 C 162 76, 174 83, 190 88" stroke="#8a6a3a" strokeWidth="1.4" strokeLinecap="round" />

        {/* leaf bunches gathered at the forks (no flutter — still air) */}
        {bunches.map((b, i) => (
          <g key={i} transform={b.t} opacity="0.95">
            <FoliageCluster width={150} leavesOnly />
          </g>
        ))}

        {/* blossoms crowning the tips */}
        <Flower x={410} y={4} s={1.3} fill="#d9a3a3" center="#b97b84" rot={14} />
        <Flower x={484} y={5} s={1} fill="#f7f2e8" center="#c3a572" rot={-18} />
        <Flower x={312} y={21} s={1.15} fill="#f7f2e8" center="#c3a572" rot={8} />
        <Flower x={382} y={6} s={0.8} fill="#ebdfd6" center="#c3a572" rot={26} />
        <Flower x={562} y={64} s={1.2} fill="#d9a3a3" center="#b97b84" rot={-12} />
        <Flower x={468} y={120} s={1.1} fill="#d9a3a3" center="#b97b84" rot={20} />
        <Flower x={474} y={124} s={0.75} fill="#ebdfd6" center="#c3a572" rot={-28} />
        <Flower x={636} y={88} s={0.95} fill="#f7f2e8" center="#c3a572" rot={16} />
        <Flower x={640} y={110} s={0.85} fill="#f7f2e8" center="#c3a572" rot={-10} />
        <Flower x={190} y={90} s={0.95} fill="#ebdfd6" center="#c3a572" rot={22} />
        <Flower x={166} y={30} s={0.85} fill="#d9a3a3" center="#b97b84" rot={-16} />
        {/* buds at the smallest tips */}
        <circle cx="360" cy="14" r="2.2" fill="#f7f2e8" opacity="0.9" />
        <circle cx="296" cy="28" r="2" fill="#d9a3a3" opacity="0.8" />
        <circle cx="448" cy="114" r="2.2" fill="#d9a3a3" opacity="0.8" />
        <circle cx="520" cy="80" r="2" fill="#f7f2e8" opacity="0.85" />
        <circle cx="584" cy="101" r="1.8" fill="#d9a3a3" opacity="0.75" />

        {/* the three that fall and grow back, seated on the bough */}
        {falling.map((f, i) => (
          <g
            key={i}
            className="fall-flower"
            style={{ animationDuration: `${f.dur}s`, animationDelay: `${f.delay}s` }}
          >
            <Flower x={f.x} y={f.y} s={f.s} fill={f.fill} center={f.center} />
          </g>
        ))}
      </g>
    </svg>
  );
}

/* Half-risen concentric rings on the footer horizon — the engagement
   motif, settling into the ground like a quiet echo */
function HorizonRings() {
  return (
    <svg
      viewBox="0 0 800 230"
      width="100%"
      preserveAspectRatio="xMidYMax meet"
      fill="none"
      aria-hidden="true"
    >
      <circle className="h-ring r1" cx="400" cy="300" r="140" stroke="#c3a572" strokeWidth="1" />
      <circle className="h-ring r2" cx="400" cy="300" r="200" stroke="#c3a572" strokeWidth="0.8" />
      <circle className="h-ring r3" cx="400" cy="300" r="262" stroke="#c3a572" strokeWidth="0.6" />
      {/* a small diamond crowning the inner ring */}
      <path
        d="M400 152 L407 160 L400 168 L393 160 Z"
        fill="#ebdfd6"
        stroke="#c3a572"
        strokeWidth="1"
        opacity="0.9"
      />
      <circle cx="400" cy="128" r="1.6" fill="#c3a572" opacity="0.7" />
      <circle cx="338" cy="170" r="1.2" fill="#c3a572" opacity="0.5" />
      <circle cx="463" cy="176" r="1.2" fill="#c3a572" opacity="0.5" />
    </svg>
  );
}

const FOOT_SPARKS = [
  { left: "4%", bottom: "12%", delay: 0, dur: 7.5 },
  { left: "11%", bottom: "30%", delay: 2.8, dur: 9 },
  { left: "18%", bottom: "8%", delay: 5.2, dur: 8 },
  { left: "26%", bottom: "42%", delay: 1.4, dur: 10 },
  { left: "33%", bottom: "16%", delay: 6.4, dur: 7 },
  { left: "41%", bottom: "55%", delay: 3.6, dur: 8.5 },
  { left: "48%", bottom: "10%", delay: 0.8, dur: 9.5 },
  { left: "56%", bottom: "36%", delay: 4.7, dur: 7.8 },
  { left: "63%", bottom: "60%", delay: 2, dur: 8.8 },
  { left: "71%", bottom: "14%", delay: 6, dur: 7.2 },
  { left: "78%", bottom: "44%", delay: 3, dur: 9.8 },
  { left: "85%", bottom: "9%", delay: 1.8, dur: 8.2 },
  { left: "91%", bottom: "28%", delay: 5.6, dur: 9.2 },
  { left: "96%", bottom: "50%", delay: 4.2, dur: 7.6 },
  { left: "8%", bottom: "58%", delay: 7.1, dur: 8.6 },
  { left: "68%", bottom: "6%", delay: 7.8, dur: 9.4 },
];

/* Two interlocked rings — the engagement motif */
function Rings({ width = 120, tone = "#c3a572", className, style }) {
  return (
    <svg
      viewBox="0 0 140 96"
      width={width}
      fill="none"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <circle cx="54" cy="54" r="30" stroke={tone} strokeWidth="2.4" />
      <circle cx="54" cy="54" r="26" stroke={tone} strokeWidth="0.8" opacity="0.5" />
      <circle cx="88" cy="54" r="30" stroke={tone} strokeWidth="2.4" />
      <circle cx="88" cy="54" r="26" stroke={tone} strokeWidth="0.8" opacity="0.5" />
      {/* diamond on the left ring */}
      <path d="M54 16 L62 24 L54 32 L46 24 Z" fill="#f7f2e8" stroke={tone} strokeWidth="1.4" />
      <path d="M48.5 24 H59.5 M54 18.5 L50 24 L54 29.5 L58 24 Z" stroke={tone} strokeWidth="0.7" opacity="0.8" />
      {/* sparkles */}
      <path d="M22 20 l2.4 5 5 2.4 -5 2.4 -2.4 5 -2.4 -5 -5 -2.4 5 -2.4 Z" fill={tone} opacity="0.75" />
      <path d="M118 30 l1.8 3.8 3.8 1.8 -3.8 1.8 -1.8 3.8 -1.8 -3.8 -3.8 -1.8 3.8 -1.8 Z" fill={tone} opacity="0.6" />
      <circle cx="112" cy="14" r="1.8" fill={tone} opacity="0.6" />
      <circle cx="30" cy="80" r="1.6" fill={tone} opacity="0.5" />
    </svg>
  );
}

export default function Engagement() {
  const [t, setT] = useState(getTimeLeft);
  const [entered, setEntered] = useState(false);
  const [musicOn, setMusicOn] = useState(false);
  const [lang, setLang] = useState(loadLang);
  const t$ = STRINGS[lang];

  const switchLang = () => {
    const next = lang === "en" ? "hi" : "en";
    setLang(next);
    try {
      localStorage.setItem("eng-lang", next);
    } catch {
      /* private mode */
    }
  };

  useEffect(() => {
    const id = setInterval(() => setT(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  const lenisRef = useRef(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    lenis.stop(); // hold the page still behind the envelope
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (entered && lenisRef.current) lenisRef.current.start();
  }, [entered]);

  /* A gentle peek a few moments after the envelope opens: the page lifts
     slightly and settles back, hinting that there is more below. Skipped
     (or abandoned) the instant the guest scrolls on their own. */
  useEffect(() => {
    if (!entered) return;
    let cancelled = false;
    let settleTimer;
    const cancel = () => {
      cancelled = true;
    };
    window.addEventListener("wheel", cancel, { passive: true, once: true });
    window.addEventListener("touchstart", cancel, { passive: true, once: true });
    window.addEventListener("keydown", cancel, { once: true });
    const peekTimer = setTimeout(() => {
      const lenis = lenisRef.current;
      if (cancelled || !lenis || window.scrollY > 2) return;
      lenis.scrollTo(84, { duration: 0.9 });
      settleTimer = setTimeout(() => {
        if (!cancelled && lenisRef.current) {
          lenisRef.current.scrollTo(0, { duration: 1 });
        }
      }, 950);
    }, 2600);
    return () => {
      clearTimeout(peekTimer);
      clearTimeout(settleTimer);
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchstart", cancel);
      window.removeEventListener("keydown", cancel);
    };
  }, [entered]);

  const heroLines = [
    { text: t$.names[0], cls: "line" },
    { text: t$.names[1], cls: "line amp" },
    { text: t$.names[2], cls: "line" },
  ];

  return (
    <div className="eng-page" lang={lang} data-testid="engagement-page">
      <Cursor />
      <EnvelopeGate
        onOpen={() => setMusicOn(true)}
        onReveal={() => setEntered(true)}
        kicker={t$.envKicker}
        names={t$.envNames}
        hint={t$.envHint}
      />
      <MusicToggle play={musicOn} labelOn={t$.musicOn} labelOff={t$.musicOff} />

      <div className="eng-petals global" aria-hidden="true">
        {Array.from({ length: 9 }).map((_, i) => (
          <span key={i} className={`eng-petal p${i + 1}`} />
        ))}
      </div>

      <button
        type="button"
        className="lang-toggle"
        onClick={switchLang}
        aria-label={lang === "en" ? "हिन्दी में देखें" : "View in English"}
        data-testid="lang-toggle"
      >
        {lang === "en" ? "हिं" : "EN"}
      </button>

      {/* ————— Hero ————— */}
      <section className="hero eng-hero" id="eng-top" data-testid="eng-hero">
        <div className="hero-foliage hf-tl">
          <FoliageCluster width={300} />
        </div>
        <div className="hero-foliage hf-tr">
          <FoliageCluster width={300} flip />
        </div>

        <div className="hero-gar" style={{ left: "5%" }}>
          <JasmineGarland rows={9} />
        </div>
        <div className="hero-gar g-extra" style={{ left: "11%" }}>
          <JasmineGarland rows={6} delay={1.2} dur={7.4} />
        </div>
        <div className="hero-gar" style={{ right: "5.5%" }}>
          <JasmineGarland rows={8} delay={0.5} dur={8.2} />
        </div>
        <div className="hero-gar g-extra" style={{ right: "11.5%" }}>
          <JasmineGarland rows={10} delay={1.7} dur={6.8} />
        </div>

        <div className="hero-chain" style={{ left: "20%" }}>
          <ChainDiya links={7} width={44} />
        </div>
        <div className="hero-chain" style={{ right: "20%" }}>
          <ChainDiya links={5} width={44} delay={0.8} dur={7.2} />
        </div>

        <div className="hero-arch">
          <PalaceArch width={560} />
        </div>

        <div className="hero-sprig sl">
          <VineBranch width={190} />
        </div>
        <div className="hero-sprig sr">
          <VineBranch width={190} flip />
        </div>

        <motion.div
          className="hero-center"
          initial="hidden"
          animate={entered ? "show" : "hidden"}
        >
          <motion.div
            className="eng-hero-kicker kicker"
            variants={{
              hidden: { opacity: 0, y: 14 },
              show: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.9, ease: EASE, delay: 0.15 },
              },
            }}
          >
            {t$.kicker}
          </motion.div>

          <motion.div
            className="eng-hero-rings"
            variants={{
              hidden: { opacity: 0, scale: 0.85 },
              show: {
                opacity: 1,
                scale: 1,
                transition: { duration: 1.1, ease: EASE, delay: 0.3 },
              },
            }}
          >
            <Rings width={120} />
          </motion.div>

          <motion.p
            className="eng-invite-line serif"
            variants={{
              hidden: { opacity: 0, y: 12 },
              show: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.9, ease: EASE, delay: 0.38 },
              },
            }}
          >
            {t$.invite1}
            {lang === "hi" ? <br /> : " "}
            {t$.invite2}
          </motion.p>

          <h1 className="names eng-names serif" data-testid="eng-hero-names">
            {heroLines.map((l, i) => (
              <span className="mask" key={`${lang}-${i}`}>
                <motion.span
                  className={l.cls}
                  variants={{
                    hidden: { y: "115%" },
                    show: {
                      y: "0%",
                      transition: {
                        duration: 1.3,
                        ease: EASE,
                        delay: 0.45 + i * 0.16,
                      },
                    },
                  }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            className="hero-date"
            data-testid="eng-hero-date"
            variants={{
              hidden: { opacity: 0, y: 18 },
              show: {
                opacity: 1,
                y: 0,
                transition: { duration: 1, ease: EASE, delay: 1.2 },
              },
            }}
          >
            <span className="date-rule" />
            <span className="eng-date-pill">{t$.heroDate}</span>
            <span className="date-rule" />
          </motion.div>

          <motion.div
            className="eng-hero-parents"
            data-testid="eng-hero-parents"
            variants={{
              hidden: { opacity: 0, y: 10 },
              show: {
                opacity: 1,
                y: 0,
                transition: { duration: 1, ease: EASE, delay: 1.65 },
              },
            }}
          >
            <span className="ehp-rule" aria-hidden="true" />
            <div className="ehp-kicker kicker">{t$.parentsKicker}</div>
            <div className="ehp-names serif">
              <span>{t$.brideParents}</span>
              <span className="ehp-sep" aria-hidden="true">
                <svg viewBox="0 0 48 24" width="34" fill="none">
                  <Flower x={24} y={12} s={0.85} center="#a5854e" />
                </svg>
              </span>
              <span>{t$.groomParents}</span>
            </div>
          </motion.div>
        </motion.div>

      </section>

      {/* ————— Countdown ————— */}
      <section className="eng-count" id="eng-count" data-testid="eng-countdown">
        <div className="eng-count-foliage cf-l" aria-hidden="true">
          <FoliageCluster width={280} leavesOnly />
        </div>
        <div className="eng-count-foliage cf-br" aria-hidden="true">
          <FoliageCluster width={280} leavesOnly />
        </div>
        <Reveal>
          <div className="kicker eng-count-kicker">{t$.countKicker}</div>
          <h2 className="serif">{t$.countTitle}</h2>
          <div className="timer" aria-live="polite" data-testid="eng-timer">
            {UNIT_KEYS.map((k) => (
              <div key={k}>
                <span className="num serif">{t[k]}</span>
                <span className="label">{t$.units[k]}</span>
              </div>
            ))}
          </div>
          <div className="divider" />
          <div className="count-note">{t$.countNote}</div>
        </Reveal>

        {/* scalloped arch edge into the rituals section */}
        <div className="eng-scallop" aria-hidden="true">
          <svg viewBox="0 0 1200 40" preserveAspectRatio="none">
            <path
              d="M0 40 L0 22
                 Q 30 -6 60 22 Q 90 -6 120 22 Q 150 -6 180 22 Q 210 -6 240 22
                 Q 270 -6 300 22 Q 330 -6 360 22 Q 390 -6 420 22 Q 450 -6 480 22
                 Q 510 -6 540 22 Q 570 -6 600 22 Q 630 -6 660 22 Q 690 -6 720 22
                 Q 750 -6 780 22 Q 810 -6 840 22 Q 870 -6 900 22 Q 930 -6 960 22
                 Q 990 -6 1020 22 Q 1050 -6 1080 22 Q 1110 -6 1140 22 Q 1170 -6 1200 22
                 L 1200 40 Z"
              fill="#f2ead9"
            />
          </svg>
        </div>
      </section>

      {/* ————— The ceremonies ————— */}
      <section className="eng-rituals" data-testid="eng-rituals">
        <div className="cele-gar" style={{ left: "3%" }}>
          <JasmineGarland rows={12} />
        </div>
        <div className="cele-gar" style={{ right: "3%" }}>
          <JasmineGarland rows={11} delay={0.6} dur={7.4} />
        </div>

        <Reveal>
          <div className="eng-sec-head">
            <div className="kicker">{t$.ritKicker}</div>
            <h2 className="serif">{t$.ritTitle}</h2>
            <div className="eng-rit-time serif">{t$.ritTime}</div>
            <div className="eng-sec-divider">
              <FloralDivider width={210} />
            </div>
          </div>
        </Reveal>

        <div className="eng-tl" data-testid="eng-timeline">
          <svg className="eng-tl-vine" aria-hidden="true">
            <defs>
              <pattern
                id="eng-vine-pat"
                width="24"
                height="120"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M12 0 C19 20 5 40 12 60 C19 80 5 100 12 120"
                  stroke="#a5854e"
                  strokeWidth="1"
                  fill="none"
                  opacity="0.75"
                />
                <path
                  d="M14 22 C19 20 22 16 22 11 C17 13 14 17 14 22 Z"
                  fill="#8a9168"
                  opacity="0.9"
                />
                <path
                  d="M10 52 C5 50 2 46 2 41 C7 43 10 47 10 52 Z"
                  fill="#7d8461"
                  opacity="0.9"
                />
                <path
                  d="M14 82 C19 80 22 76 22 71 C17 73 14 77 14 82 Z"
                  fill="#98a073"
                  opacity="0.9"
                />
                <path
                  d="M10 105 C5 103 2 99 2 94 C7 96 10 100 10 105 Z"
                  fill="#8a9168"
                  opacity="0.9"
                />
                <circle cx="10" cy="33" r="1.4" fill="#c3a572" />
                <circle cx="15" cy="66" r="1.4" fill="#c3a572" />
                <circle cx="10" cy="95" r="1.4" fill="#c3a572" />
              </pattern>
            </defs>
            <rect width="24" height="100%" fill="url(#eng-vine-pat)" />
          </svg>
          {t$.ceremonies
            .map((c) => ({
              key: c.key,
              name: c.name,
              note: c.note,
              quote: c.quote,
            }))
            .map((e, i) => (
            <Reveal key={e.key} delay={Math.min(i * 0.05, 0.2)}>
              <div
                className={`eng-tl-item${i % 2 === 1 ? " right" : ""}`}
                data-testid={`eng-${e.key}`}
              >
                <span className="eng-tl-dot" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="24" fill="none">
                    <Flower x={12} y={12} s={0.72} fill="#f2ead9" center="#a5854e" />
                  </svg>
                </span>
                <div className="eng-tl-card">
                  <h3 className="eng-tl-name serif">{e.name}</h3>
                  {e.note && <div className="eng-tl-note">{e.note}</div>}
                  <p className="eng-tl-quote">{e.quote}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ————— Venue ————— */}
      <section className="details eng-venue" data-testid="eng-venue">
        <div className="eng-fam-bg" aria-hidden="true">
          <span className="fam-blob fb1" />
          <span className="fam-blob fb2" />
          <span className="fam-blob fb3" />
        </div>
        <div className="eng-branch" aria-hidden="true">
          <WindBranch />
        </div>
        <Reveal>
          <div className="kicker details-kicker">{t$.venueKicker}</div>
          <div className="details-frame">
            {["tl", "tr", "bl", "br"].map((c) => (
              <span key={c} className={`fdot fdot-${c}`} aria-hidden="true" />
            ))}
            <CornerFlourish size={54} className="eng-corner c-tl" />
            <CornerFlourish
              size={54}
              className="eng-corner c-br"
              leafFill="rgba(138, 145, 104, 0.8)"
            />
            <div className="eng-venue-rings" aria-hidden="true">
              <Rings width={78} />
            </div>
            <div className="details-sprig details-sprig-l">
              <VineBranch width={120} flip />
            </div>
            <div className="details-sprig details-sprig-r">
              <VineBranch width={120} />
            </div>
            <h2 className="venue serif">{t$.venueName}</h2>
            <div className="address">
              {t$.venueAddr1}
              <br />
              {t$.venueAddr2}
            </div>
            <div className="eng-venue-time">{t$.venueTime}</div>

            <a
              className="eng-wa-btn"
              data-testid="eng-rsvp-link"
              href={RSVP_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="eng-btn-label">{t$.rsvpBtn}</span>
              <span className="eng-btn-bush" aria-hidden="true">
                <svg viewBox="0 0 120 70" width="120" fill="none">
                  <Flower x={100} y={56} s={1.6} fill="#f7f2e8" center="#c3a572" rot={10} />
                  <Flower x={72} y={64} s={1.15} fill="#d9a3a3" center="#b97b84" rot={-15} />
                  <Flower x={110} y={28} s={1.25} fill="#d9a3a3" center="#b97b84" rot={20} />
                  <Flower x={86} y={36} s={0.9} fill="#f7f2e8" center="#c3a572" />
                  <Flower x={58} y={48} s={0.7} fill="#ebdfd6" center="#c3a572" rot={30} />
                  <circle cx={48} cy={60} r={2} fill="#f7f2e8" opacity="0.8" />
                  <circle cx={98} cy={12} r={1.6} fill="#d9a3a3" opacity="0.8" />
                </svg>
              </span>
            </a>

            <div className="eng-venue-actions">
              <a
                className="map-btn"
                data-testid="eng-map-link"
                href="https://www.google.com/maps/search/?api=1&query=Hotel+SB+Castle+Govind+Nagar+Bridge+Fazalganj+Kanpur"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t$.mapBtn}
              </a>
              <a
                className="map-btn"
                data-testid="eng-gcal-link"
                href={GCAL_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t$.calGoogle}
              </a>
              <a className="map-btn" data-testid="eng-ics-link" href="/sagai.ics" download>
                {t$.calApple}
              </a>
            </div>
          </div>
        </Reveal>

        {/* scalloped ivory hem rising into the burgundy */}
        <div className="eng-tear" aria-hidden="true">
          <svg viewBox="0 0 1200 40" preserveAspectRatio="none">
            <path
              d="M0 40 L0 22
                 Q 30 -6 60 22 Q 90 -6 120 22 Q 150 -6 180 22 Q 210 -6 240 22
                 Q 270 -6 300 22 Q 330 -6 360 22 Q 390 -6 420 22 Q 450 -6 480 22
                 Q 510 -6 540 22 Q 570 -6 600 22 Q 630 -6 660 22 Q 690 -6 720 22
                 Q 750 -6 780 22 Q 810 -6 840 22 Q 870 -6 900 22 Q 930 -6 960 22
                 Q 990 -6 1020 22 Q 1050 -6 1080 22 Q 1110 -6 1140 22 Q 1170 -6 1200 22
                 L 1200 40 Z"
              fill="#f4eee3"
            />
          </svg>
        </div>
      </section>

      {/* ————— Footer ————— */}
      <footer className="eng-foot" data-testid="eng-footer">
        <Reveal>
          <div className="eng-foot-rings">
            <Rings width={86} tone="#c3a572" />
          </div>
          <p className="eng-foot-line serif">{t$.footLine}</p>
          <div className="eng-foot-fam">
            {t$.footFam1}
            <br />
            {t$.footFam2}
          </div>
        </Reveal>

        <div className="eng-foot-deco" aria-hidden="true">
          <span className="foot-glow" />
          <div className="foot-bush b-l">
            <FoliageCluster width={300} />
          </div>
          <div className="foot-bush b-r">
            <FoliageCluster width={300} />
          </div>
          <span className="foot-orb o1" />
          <span className="foot-orb o2" />
          <span className="foot-orb o3" />
          {FOOT_SPARKS.slice(0, 8).map((s, i) => (
            <span
              key={i}
              className="foot-spark"
              style={{
                left: s.left,
                bottom: s.bottom,
                animationDelay: `${s.delay}s`,
                animationDuration: `${s.dur}s`,
              }}
            />
          ))}
          <div className="foot-horizon">
            <HorizonRings />
          </div>
        </div>
      </footer>

      <div className="grain" aria-hidden="true" />
    </div>
  );
}
